import React, { useCallback, useEffect, useState } from 'react';
import { useToast } from '../sectepe-ui/index';
import auswertungService, { Uebersicht, Zeile, alsProzent } from '../services/auswertungService';
import { PageHeader, EmptyState, LoadingState, StatusBadge, RelatedLinks } from '../components/ui';
import { Icon } from '../components/icons';
import { SalesProcessBar } from '../vertrieb/SalesProcessBar';
import { KpiRow } from '../vertrieb/KpiRow';
import { fehlerText } from '../vertrieb/format';
import { RechteHinweis } from '../vertrieb/RechteHinweis';
import { useKampagnenZuordnung, kampagnenLinks } from '../vertrieb/kampagnen';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';

/**
 * Kampagnenauswertung.
 *
 * Wichtig fuer das Lesen der Tabelle: bei LinkedIn-Kampagnen fuehrt Lemlist
 * unter "geoeffnet" und "geklickt" etwas anderes als bei E-Mail. Diese
 * Spalten bleiben dort leer, statt eine erfundene Quote auszuweisen.
 */

const zahl = (n: number) => n.toLocaleString('de-DE');

/** Punktedifferenz mit Vorzeichen, z. B. "+2,4 Pp". */
const punkte = (wert: number | null | undefined): string => {
  if (wert === null || wert === undefined) return '';
  const p = wert * 100;
  return `${p >= 0 ? '+' : ''}${p.toFixed(1).replace('.', ',')} Pp`;
};

const KanalBadge: React.FC<{ kanal: string }> = ({ kanal }) => (
  <StatusBadge tone={kanal === 'linkedin' ? 'info' : 'neutral'} icon={kanal === 'linkedin' ? 'linkedin' : 'mail'}>
    {kanal === 'linkedin' ? 'LinkedIn' : 'E-Mail'}
  </StatusBadge>
);

const AuswertungPage: React.FC = () => {
  const { push } = useToast();
  const zuordnung = useKampagnenZuordnung();
  // Welle 2: "Zahlen holen" ist POST /auswertung/sammeln (vertrieb.bearbeiten).
  const { darf } = useBerechtigung();
  const darfSammeln = darf('vertrieb.bearbeiten');
  const siehtPostfaecher = darf('versand.lesen');
  const [daten, setDaten] = useState<Uebersicht | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [sammelt, setSammelt] = useState(false);
  const [auchLeere, setAuchLeere] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = useCallback(async () => {
    setLaedt(true);
    setFehler(null);
    try {
      setDaten((await auswertungService.uebersicht(auchLeere)) ?? null);
    } catch (e) {
      setFehler(fehlerText(e, 'Auswertung nicht lesbar'));
    } finally {
      setLaedt(false);
    }
  }, [auchLeere]);

  useEffect(() => { laden(); }, [laden]);

  const sammeln = async () => {
    setSammelt(true);
    setFehler(null);
    try {
      const b = await auswertungService.sammeln();
      push({
        message: `${b.geschrieben} von ${b.kampagnen} Kampagnen abgeholt`
          + (b.ohneZahlen ? `, ${b.ohneZahlen} ohne Zahlen` : '')
          + (b.fehler.length ? `, ${b.fehler.length} Fehler` : ''),
        severity: b.fehler.length ? 'warning' : 'success',
        duration: 8000,
      });
      await laden();
    } catch (e) {
      setFehler(fehlerText(e, 'Zahlen konnten nicht geholt werden'));
    } finally {
      setSammelt(false);
    }
  };

  const zeile = (z: Zeile) => {
    const warnung = z.hinweise.some((h) => h.schwere === 'warnung');
    return (
      <React.Fragment key={z.lemlistCampaignId}>
        <tr className={warnung ? 'zeile-warnung' : ''}>
          <td>
            <div className="ausw-name">{z.name}</div>
            {z.lemlistCampaignId && kampagnenLinks(z.lemlistCampaignId, zuordnung, { leads: true }).length > 0 && (
              <RelatedLinks items={kampagnenLinks(z.lemlistCampaignId, zuordnung, { leads: true })} label={`Verknüpft mit ${z.name}`} />
            )}
            <div className="ausw-unterzeile">
              <KanalBadge kanal={z.kanal} />
              {z.status && <span className="ausw-status">{z.status}</span>}
            </div>
          </td>
          <td className="zahl">{zahl(z.zahlen.sentCount)}</td>
          <td className="zahl">
            {zahl(z.zahlen.deliveredCount)}
            {z.quoten.nichtZugestellt !== null && z.quoten.nichtZugestellt > 0 && (
              <div className={`ausw-quote ${warnung ? 'ausw-quote-warnung' : ''}`}>
                {alsProzent(z.quoten.nichtZugestellt)} daneben
              </div>
            )}
          </td>
          <td className="zahl">
            {/* Bei LinkedIn traegt openedCount nicht die Oeffnungen. */}
            {z.kanal === 'linkedin' ? '–' : zahl(z.zahlen.openedCount)}
            {z.quoten.geoeffnet !== null && (
              <div className="ausw-quote">{alsProzent(z.quoten.geoeffnet)}</div>
            )}
          </td>
          <td className="zahl">
            {zahl(z.zahlen.repliedCount)}
            {z.quoten.geantwortet !== null && (
              <div className="ausw-quote">{alsProzent(z.quoten.geantwortet)}</div>
            )}
          </td>
          <td className="zahl">
            {z.veraenderung ? (
              <>
                <div>{z.veraenderung.versendet > 0 ? `+${zahl(z.veraenderung.versendet)}` : '0'}</div>
                {z.veraenderung.nichtZugestelltPunkte !== null
                  && Math.abs(z.veraenderung.nichtZugestelltPunkte) >= 0.005 && (
                  <div className={z.veraenderung.nichtZugestelltPunkte > 0 ? 'ausw-quote-warnung' : 'ausw-quote'}>
                    {punkte(z.veraenderung.nichtZugestelltPunkte)}
                  </div>
                )}
              </>
            ) : (
              // Ein einzelner Stand ist noch kein Verlauf.
              <span className="ausw-quote">erster Stand</span>
            )}
          </td>
        </tr>
        {z.hinweise.length > 0 && (
          <tr className="ausw-hinweis-zeile">
            <td colSpan={6}>
              {z.hinweise.map((h, i) => (
                <div key={i} className={h.schwere === 'warnung' ? 'ausw-warnung' : 'ausw-hinweis'}>
                  {h.text}
                </div>
              ))}
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className="page-container-wide">
      <SalesProcessBar stepId="auswertung" />
      <PageHeader
        title="Auswertung"
        subtitle="Was aus den Kampagnen tatsächlich herauskam. Lemlist gibt nur den Stand von jetzt heraus – die Tagesstände hier machen daraus einen Verlauf, und erst der zeigt, ob die Zustellbarkeit kippt."
        actions={darfSammeln ? (
          <button type="button" className="btn btn-primary" onClick={sammeln} disabled={sammelt}>
            <Icon name="refresh" size={14} /> {sammelt ? 'Holt Zahlen…' : 'Zahlen bei Lemlist holen'}
          </button>
        ) : undefined}
      />
      <RechteHinweis was="Zahlen bei Lemlist holen" />

      {fehler && <div className="alert alert-error">{fehler}</div>}

      {daten && daten.zeilen.length > 0 && (() => {
        const nichtZugestellt = daten.summe.email.versendet
          ? (daten.summe.email.versendet - daten.summe.email.zugestellt) / daten.summe.email.versendet
          : null;
        return (
          <KpiRow
            aria-label="Kampagnen-Kennzahlen"
            items={[
              { label: 'E-Mails versendet', value: zahl(daten.summe.email.versendet) },
              { label: 'nicht zugestellt', value: alsProzent(nichtZugestellt), tone: nichtZugestellt !== null && nichtZugestellt > 0.05 ? 'bad' : 'neutral', hint: nichtZugestellt !== null && nichtZugestellt > 0.05 ? 'über 5 % – Postfächer prüfen' : undefined, to: siehtPostfaecher && nichtZugestellt !== null && nichtZugestellt > 0.05 ? '/mailboxes' : undefined },
              { label: 'Antwortquote E-Mail', value: alsProzent(daten.summe.email.zugestellt ? daten.summe.email.geantwortet / daten.summe.email.zugestellt : null), tone: 'good' },
              { label: 'Antworten LinkedIn', value: zahl(daten.summe.linkedin.geantwortet), to: '/outreach-inbox?ansicht=antworten' },
            ]}
          />
        );
      })()}

      <div className="ausw-leiste">
        <label>
          <input
            type="checkbox"
            checked={auchLeere}
            onChange={(e) => setAuchLeere(e.target.checked)}
          />{' '}
          Entwürfe ohne Versand mitzeigen
        </label>
        {daten?.stand && <span className="ausw-stand">Stand: {daten.stand}</span>}
      </div>

      {laedt && !daten && <LoadingState rows={4} label="Auswertung wird geladen" />}

      {!laedt && daten && daten.zeilen.length === 0 && (
        <EmptyState
          icon="pie-chart"
          title="Noch keine Zahlen"
          description={darfSammeln
            ? 'Einmal „Zahlen bei Lemlist holen“ – danach schreibt der tägliche Lauf sie fort. Ab dem zweiten Stand siehst du hier den Verlauf.'
            : 'Der tägliche Lauf holt die Zahlen bei Lemlist. Ab dem zweiten Stand siehst du hier den Verlauf.'}
          action={darfSammeln ? { label: 'Zahlen bei Lemlist holen', onClick: sammeln } : undefined}
          secondaryAction={{ label: 'Zum Cockpit', to: '/vertrieb' }}
        />
      )}

      {daten && daten.zeilen.length > 0 && (
        <div className="table-container">
        <table className="table ausw-tabelle">
          <thead>
            <tr>
              <th>Kampagne</th>
              <th className="zahl">versendet</th>
              <th className="zahl">zugestellt</th>
              <th className="zahl">geöffnet</th>
              <th className="zahl">geantwortet</th>
              <th className="zahl">seit letztem Stand</th>
            </tr>
          </thead>
          <tbody>{daten.zeilen.map(zeile)}</tbody>
        </table>
        </div>
      )}
    </div>
  );
};

export default AuswertungPage;
